"use client"

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const indicatorVariants = cva(
  "absolute bottom-0 left-0 h-(--active-tab-height) w-(--active-tab-width) translate-x-(--active-tab-left) -translate-y-(--active-tab-bottom) transition-[width,translate] duration-350 ease-out-expo",
  {
    variants: {
      variant: {
        default: "-z-1 rounded-md",
        pill: "-z-1 rounded-full",
        underline:
          "z-10 data-[orientation=horizontal]:h-0.5 data-[orientation=horizontal]:translate-y-px data-[orientation=vertical]:w-0.5 data-[orientation=vertical]:-translate-x-px",
      },
      indicatorColor: {
        primary: "",
        secondary: "",
      },
    },
    compoundVariants: [
      { variant: "default", indicatorColor: "primary", className: "bg-primary dark:bg-primary" },
      { variant: "pill", indicatorColor: "primary", className: "bg-primary dark:bg-primary" },
      {
        variant: "default",
        indicatorColor: "secondary",
        className: "bg-muted-foreground/30 dark:bg-muted-foreground/35",
      },
      {
        variant: "pill",
        indicatorColor: "secondary",
        className: "bg-muted-foreground/30 dark:bg-muted-foreground/35",
      },
      { variant: "underline", indicatorColor: "primary", className: "bg-foreground" },
      {
        variant: "underline",
        indicatorColor: "secondary",
        className: "bg-muted-foreground/55 dark:bg-muted-foreground/50",
      },
    ],
    defaultVariants: {
      variant: "default",
      indicatorColor: "primary",
    },
  }
)

function Tabs({ className, ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn(
        "flex flex-col gap-0 data-[orientation=vertical]:flex-row",
        className
      )}
      {...props}
    />
  )
}

function TabsList({
  variant = "default",
  indicatorColor = "primary",
  className,
  children,
  ...props
}: TabsPrimitive.List.Props & VariantProps<typeof indicatorVariants>) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "relative z-0 flex w-fit items-center justify-center gap-x-0.5 text-muted-foreground",
        "data-[orientation=vertical]:flex-col",
        variant === "default"
          ? "rounded-lg bg-muted/50 p-1 text-foreground"
          : variant === "pill"
            ? "rounded-full bg-muted/50 p-1 text-foreground *:data-[slot=tabs-trigger]:rounded-full shadow-sm"
            : "data-[orientation=horizontal]:py-1 data-[orientation=vertical]:px-1 *:data-[slot=tabs-trigger]:hover:bg-primary/10",
        className
      )}
      {...props}
    >
      {children}
      <TabsPrimitive.Indicator
        data-slot="tab-indicator"
        className={indicatorVariants({ variant, indicatorColor })}
      />
    </TabsPrimitive.List>
  )
}

function TabsTab({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "flex flex-1 shrink-0 cursor-pointer items-center justify-center rounded-md border border-transparent text-sm font-medium whitespace-nowrap transition-[color,background-color,box-shadow] outline-none focus-visible:ring-2 focus-visible:ring-ring data-disabled:pointer-events-none data-disabled:opacity-64 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        "data-selected:text-foreground",
        "gap-1.5 px-[calc(--spacing(2.5)-1px)] py-[calc(--spacing(1.5)-1px)]",
        "data-[orientation=vertical]:w-full data-[orientation=vertical]:justify-start",
        className
      )}
      {...props}
    />
  )
}

function TabsPanel({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  )
}

export {
  Tabs,
  TabsList,
  TabsTab,
  TabsTab as TabsTrigger,
  TabsPanel,
  TabsPanel as TabsContent,
}
